import { PromiseClient } from 'hub-http/adapters/promiseClient';
import { GetTypesBatchByFeaturesResponse } from 'framework-builder-read-service-client/FrameworkBuilderReadService/types';
import { makeTypeMetadataEntriesFetchUrl } from './cacheKeys';
import {
  getTypeMetadataQuickFetches,
  takeTypeMetadataQuickFetch,
} from './typeMetadataQuickFetch';
import { AppSettingTypeMap } from './AppSettingTypeMap';
import { keyEntriesByAppSettings } from './keyEntriesByAppSettings';
import { batchScheduler } from 'batch-promise';
import { unwrapResult } from './unwrapResult';

const maybeFetchTypeEntries = ({
  appSettingNames,
  httpClient,
}: {
  appSettingNames: string[];
  httpClient: PromiseClient;
}) => {
  if (appSettingNames.length === 0) {
    return Promise.resolve({} as AppSettingTypeMap);
  }

  return httpClient
    .post<GetTypesBatchByFeaturesResponse>(makeTypeMetadataEntriesFetchUrl(), {
      data: appSettingNames,
    })
    .then(keyEntriesByAppSettings);
};

const fetchTypeMetadataEntriesWithQuickFetch = async ({
  appSettingNames,
  httpClient,
}: {
  appSettingNames: string[];
  httpClient: PromiseClient;
}) => {
  const quickFetches = getTypeMetadataQuickFetches();

  const batchFetch = maybeFetchTypeEntries({
    httpClient,
    appSettingNames: appSettingNames.filter(
      (appSettingName) => !quickFetches.has(appSettingName)
    ),
  });

  const results = await Promise.allSettled(
    appSettingNames.map((appSettingName) => {
      const quickFetched = takeTypeMetadataQuickFetch(appSettingName) as
        | Promise<AppSettingTypeMap>
        | undefined;

      return (
        !quickFetched
          ? batchFetch
          : quickFetched.catch(() =>
              maybeFetchTypeEntries({
                httpClient,
                appSettingNames: [appSettingName],
              })
            )
      ).then((result) => result[appSettingName]);
    })
  );

  return Object.fromEntries(
    appSettingNames.map((name, index) => [name, results[index]])
  );
};

export const batchedTypeMetadataRequest = batchScheduler()
  .read<{ appSettingName: string; httpClient: PromiseClient }>()
  .identity((req) => req.appSettingName)
  .partition(({ httpClient }) => httpClient)
  .execute((requestMap, client) =>
    fetchTypeMetadataEntriesWithQuickFetch({
      appSettingNames: Object.keys(requestMap),
      httpClient: client,
    })
  )
  .extract(({ response, identity }) =>
    response[identity] ? unwrapResult(response[identity]) : null
  )
  .window(10);
